/**
 * constants.js — Single source of truth for all enums in the app.
 *
 * HOW TO ADD A NEW ITEM:
 *   1. Find the section (e.g. "LEAVE STATUS").
 *   2. Add ONE line to the config object.
 *   3. Done — enum value, label, CSS class, helpers, and dropdown
 *      options all auto-generate.
 *
 * HOW TO ADD A NEW ENUM:
 *   1. Import defineEnum (already imported at the top).
 *   2. Copy any existing section as a template.
 *   3. Update the config, rename the exports.
 */

import { defineEnum } from "./defineEnum";

// ============================================================
// USER STATUS
// ============================================================
const _userStatus = defineEnum({
  ACTIVE: { value: true, label: "Active", class: "active" },
  INACTIVE: { value: false, label: "Inactive", class: "inactive" },
});
export const UserStatus = _userStatus.values;
export const UserStatusMeta = _userStatus.meta;
export const getUserStatusLabel = _userStatus.getLabel;
export const getUserStatusClass = _userStatus.getClass;
export const UserStatusOptions = _userStatus.options;
export const UserStatusFilterOptions = _userStatus.filterOptions;

// ============================================================
// DEPARTMENT STATUS
// ============================================================
const _departmentStatus = defineEnum({
  ACTIVE: { value: 1, label: "Active", class: "active" },
  INACTIVE: { value: 2, label: "Inactive", class: "inactive" },
});
export const DepartmentStatus = { ALL: 0, ..._departmentStatus.values };
export const DepartmentStatusMeta = _departmentStatus.meta;

// Department `is_active` comes from the backend as a boolean, so the
// lookup is truthiness-based rather than a direct Meta key lookup.
export const getDepartmentStatusLabel = (status) =>
  status
    ? DepartmentStatusMeta[DepartmentStatus.ACTIVE].label
    : DepartmentStatusMeta[DepartmentStatus.INACTIVE].label;
export const getDepartmentStatusClass = (status) =>
  status
    ? DepartmentStatusMeta[DepartmentStatus.ACTIVE].class
    : DepartmentStatusMeta[DepartmentStatus.INACTIVE].class;

export const DepartmentStatusOptions = [
  { value: DepartmentStatus.ALL, label: "All" },
  ..._departmentStatus.options,
];
// ============================================================
// DESIGNATION STATUS
// ============================================================
const _designationStatus = defineEnum({
  ACTIVE: { value: true, label: "Active", class: "active" },
  INACTIVE: { value: false, label: "Inactive", class: "inactive" },
});
export const DesignationStatus = _designationStatus.values;
export const DesignationStatusMeta = _designationStatus.meta;
export const getDesignationStatusLabel = _designationStatus.getLabel;
export const getDesignationStatusClass = _designationStatus.getClass;
export const DesignationStatusOptions = _designationStatus.options;
export const DesignationStatusFilterOptions = _designationStatus.filterOptions;
// ============================================================
// ATTENDANCE STATUS
// ============================================================
const _attendanceStatus = defineEnum({
  PRESENT: { value: 1, label: "Present", class: "present" },
  ABSENT: { value: 2, label: "Absent", class: "absent" },
  HALF_DAY: { value: 3, label: "Half Day", class: "half-day" },
  ON_LEAVE: { value: 4, label: "On Leave", class: "on-leave" },
  WORK_FROM_HOME: { value: 5, label: "WFH", class: "wfh" },
  // HOLIDAY: { value: 6, label: "Holiday", class: "holiday" },
  // WEEKEND: { value: 7, label: "Weekend", class: "weekend" },
});
export const AttendanceStatus = _attendanceStatus.values;
export const AttendanceStatusMeta = _attendanceStatus.meta;
export const getAttendanceStatusLabel = _attendanceStatus.getLabel;
export const getAttendanceStatusClass = _attendanceStatus.getClass;
export const AttendanceStatusOptions = _attendanceStatus.options;
export const AttendanceStatusFilterOptions = _attendanceStatus.filterOptions;

// Curated subset for bulk-mark / manual-mark forms — excludes
// system-driven statuses that HR doesn't assign manually.
const EXCLUDED_FROM_BULK_FORM = [
  // AttendanceStatus.HOLIDAY,
  // AttendanceStatus.WEEKEND,
];
export const AttendanceStatusFormOptions = AttendanceStatusOptions.filter(
  (o) => !EXCLUDED_FROM_BULK_FORM.includes(o.value)
);

// ============================================================
// REGULARIZATION STATUS
// ============================================================
const _regularizationStatus = defineEnum({
  PENDING: { value: 1, label: "Pending", class: "on-hold" },
  APPROVED: { value: 2, label: "Approved", class: "active" },
  REJECTED: { value: 3, label: "Rejected", class: "inactive" },
});
export const RegularizationStatus = _regularizationStatus.values;
export const RegularizationStatusMeta = _regularizationStatus.meta;
export const getRegularizationStatusLabel = _regularizationStatus.getLabel;
export const getRegularizationStatusClass = _regularizationStatus.getClass;
export const RegularizationStatusOptions = _regularizationStatus.options;
export const RegularizationStatusFilterOptions = _regularizationStatus.filterOptions;

// ============================================================
// LEAVE REQUEST STATUS (internal — not yet wired to UI dropdowns)
// ============================================================
export const LeaveRequestStatus = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
  CANCELLED: 4,
};

// ============================================================
// TRACE TYPE
// ============================================================
export const TraceType = {
  IN: 1,
  OUT: 2,
  BREAK_START: 3,
  BREAK_END: 4,
};

// ============================================================
// LEAVE DAY PART
// ============================================================
const _leaveDayPart = defineEnum({
  FULL_DAY: { value: 1, label: "Full Day", class: "full-day" },
  FIRST_HALF: { value: 2, label: "First Half", class: "first-half" },
  SECOND_HALF: { value: 3, label: "Second Half", class: "second-half" },
});
export const LeaveDayPart = _leaveDayPart.values;
export const LeaveDayPartMeta = _leaveDayPart.meta;
export const LeaveDayPartOptions = _leaveDayPart.options;

// Legacy string → numeric id map. Old leave requests stored day_part
// as snake_case strings. This lets helpers render old data correctly.
const LEGACY_DAY_PART_STRINGS = {
  full_day: LeaveDayPart.FULL_DAY,
  first_half: LeaveDayPart.FIRST_HALF,
  second_half: LeaveDayPart.SECOND_HALF,
};
export const getLeaveDayPartLabel = (d) => {
  const normalized = LEGACY_DAY_PART_STRINGS[d] ?? d;
  return LeaveDayPartMeta[normalized]?.label ?? d;
};
export const getLeaveDayPartClass = (d) => {
  const normalized = LEGACY_DAY_PART_STRINGS[d] ?? d;
  return LeaveDayPartMeta[normalized]?.class ?? "";
};

// ============================================================
// DSR STATUS
// ============================================================
// export const DSRStatus = {
//   DRAFT: 1,
//   SUBMITTED: 2,
//   REVIEWED: 3,
//   REJECTED: 4,
// };
// ============================================================
// DSR STATUS
// ============================================================
const _dsrStatus = defineEnum({
  COMPLETED: { value: 1, label: "Completed", class: "active" },
  IN_PROGRESS: { value: 2, label: "In Progress", class: "on-hold" },
  BLOCKED: { value: 3, label: "Blocked", class: "inactive" },
});
export const DSRStatus = _dsrStatus.values;
export const DSRStatusMeta = _dsrStatus.meta;
export const getDSRStatusLabel = _dsrStatus.getLabel;
export const getDSRStatusClass = _dsrStatus.getClass;
export const DSRStatusOptions = _dsrStatus.options;
export const DSRStatusFilterOptions = _dsrStatus.filterOptions;
// ============================================================
// EMPLOYEE STATUS
// ============================================================
const _employeeStatus = defineEnum({
  ACTIVE: { value: 1, label: "Active", class: "active" },
  INACTIVE: { value: 2, label: "Inactive", class: "inactive" },
  ON_LEAVE: { value: 3, label: "On Leave", class: "on-leave" },
  TERMINATED: { value: 4, label: "Terminated", class: "terminated" },
  RESIGNED: { value: 5, label: "Resigned", class: "resigned" },
  PROBATION: { value: 6, label: "Probation", class: "probation" },
});
export const EmployeeStatus = _employeeStatus.values;
export const EmployeeStatusMeta = _employeeStatus.meta;
export const getEmployeeStatusLabel = _employeeStatus.getLabel;
export const getEmployeeStatusClass = _employeeStatus.getClass;
export const EmployeeStatusOptions = _employeeStatus.options;
export const EmployeeStatusFilterOptions = _employeeStatus.filterOptions;

// ============================================================
// GENDER
// ============================================================
const _gender = defineEnum({
  MALE: { value: 1, label: "Male", class: "male" },
  FEMALE: { value: 2, label: "Female", class: "female" },
  OTHER: { value: 3, label: "Other", class: "other" },
});
export const Gender = _gender.values;
export const GenderMeta = _gender.meta;
export const getGenderLabel = _gender.getLabel;
export const getGenderClass = _gender.getClass;
export const GenderOptions = _gender.options;
export const GenderFilterOptions = _gender.filterOptions;

// ============================================================
// DOCUMENT TYPE
// ============================================================
const _documentType = defineEnum({
  AADHAAR: { value: 1, label: "Aadhaar Card", class: "aadhaar" },
  PAN: { value: 2, label: "PAN Card", class: "pan" },
  PASSPORT: { value: 3, label: "Passport", class: "passport" },
  DRIVING_LICENSE: { value: 4, label: "Driving License", class: "driving-license" },
  VOTER_ID: { value: 5, label: "Voter ID", class: "voter-id" },
  OFFER_LETTER: { value: 6, label: "Offer Letter", class: "offer-letter" },
  JOINING_LETTER: { value: 7, label: "Joining Letter", class: "joining-letter" },
  CONTRACT: { value: 8, label: "Contract", class: "contract" },
  EXPERIENCE_LETTER: { value: 9, label: "Experience Letter", class: "experience-letter" },
  RELIEVING_LETTER: { value: 10, label: "Relieving Letter", class: "relieving-letter" },
  PAYSLIP: { value: 11, label: "Salary Slip", class: "payslip" },
  RESUME: { value: 12, label: "Resume", class: "resume" },
  PHOTO: { value: 13, label: "Photo", class: "photo" },
  OTHER: { value: 14, label: "Other", class: "other" },
});
export const DocumentType = _documentType.values;
export const DocumentTypeMeta = _documentType.meta;
export const getDocumentTypeLabel = _documentType.getLabel;
export const getDocumentTypeClass = _documentType.getClass;
export const DocumentTypeOptions = _documentType.options;
export const DocumentTypeFilterOptions = _documentType.filterOptions;

// Curated subset — excludes types captured elsewhere (profile photo
// has its own dedicated upload).
const EXCLUDED_FROM_DOCUMENT_FORM = [DocumentType.PHOTO];
export const DocumentTypeFormOptions = DocumentTypeOptions.filter(
  (o) => !EXCLUDED_FROM_DOCUMENT_FORM.includes(o.value)
);

// ============================================================
// CLIENT
// ============================================================
export const ClientStatus = {
  ACTIVE: 1,
  INACTIVE: 2,
  ARCHIVED: 3,
};

export const ClientIndustry = {
  TECHNOLOGY: 1,
  HEALTHCARE: 2,
  FINANCE: 3,
  EDUCATION: 4,
  RETAIL: 5,
  MANUFACTURING: 6,
  OTHER: 7,
};

// ============================================================
// PROJECT
// ============================================================
export const ProjectStatus = {
  PLANNING: 1,
  ACTIVE: 2,
  ON_HOLD: 3,
  COMPLETED: 4,
  CANCELLED: 5,
};

export const ProjectPriority = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  CRITICAL: 4,
};

export const ProjectMemberRole = {
  MANAGER: 1,
  LEAD: 2,
  DEVELOPER: 3,
  DESIGNER: 4,
  QA: 5,
  OTHER: 6,
};

// ============================================================
// TASK
// ============================================================
export const TaskPriority = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
};

export const TaskStatus = {
  TODO: 1,
  IN_PROGRESS: 2,
  DONE: 3,
  CANCELLED: 4,
  ON_HOLD: 5,
};

// ============================================================
// TEAM
// ============================================================
export const TeamMemberRole = {
  LEAD: 1,
  DEVELOPER: 2,
  DESIGNER: 3,
  TESTER: 4,
  ANALYST: 5,
  DEVOPS: 6,
  MEMBER: 7,
};

// ============================================================
// SHIFT
// ============================================================
export const ShiftType = {
  FIXED: 1,
  FLEXIBLE: 2,
};

// ============================================================
// HOLIDAY
// ============================================================
export const HolidayType = {
  NATIONAL: 1,
  REGIONAL: 2,
  COMPANY: 3,
};

// ============================================================
// DATA SCOPE
// ============================================================
export const DataScopeLevel = {
  SELF: 1,
  REPORTEES: 2,
  DEPARTMENT: 3,
  ALL: 4,
};

// ============================================================
// LEAVE TYPE
// ============================================================
const _leaveType = defineEnum({
  CASUAL_LEAVE: { value: 1, label: "Casual Leave", class: "casual-leave" },
  SICK_LEAVE: { value: 2, label: "Sick Leave", class: "sick-leave" },
  EARNED_LEAVE: { value: 3, label: "Earned Leave", class: "earned-leave" },
  MATERNITY_LEAVE: { value: 4, label: "Maternity Leave", class: "maternity-leave" },
  PATERNITY_LEAVE: { value: 5, label: "Paternity Leave", class: "paternity-leave" },
  COMPENSATORY_OFF: { value: 6, label: "Compensatory Off", class: "compensatory-off" },
  LOSS_OF_PAY: { value: 7, label: "Loss of Pay", class: "loss-of-pay" },
  WORK_FROM_HOME: { value: 8, label: "Work From Home", class: "wfh" },
});
export const LeaveType = _leaveType.values;
export const LeaveTypeMeta = _leaveType.meta;
export const getLeaveTypeLabel = _leaveType.getLabel;
export const getLeaveTypeClass = _leaveType.getClass;
export const LeaveTypeOptions = _leaveType.options;
export const LeaveTypeFilterOptions = _leaveType.filterOptions;

// ============================================================
// LEAVE STATUS
// ============================================================
const _leaveStatus = defineEnum({
  PENDING: { value: 1, label: "Pending", class: "on-hold" },
  APPROVED: { value: 2, label: "Approved", class: "active" },
  REJECTED: { value: 3, label: "Rejected", class: "inactive" },
  CANCELLED: { value: 4, label: "Cancelled", class: "inactive" },
});
export const LeaveStatus = _leaveStatus.values;
export const LeaveStatusMeta = _leaveStatus.meta;
export const getLeaveStatusLabel = _leaveStatus.getLabel;
export const getLeaveStatusClass = _leaveStatus.getClass;
export const LeaveStatusOptions = _leaveStatus.options;
export const LeaveStatusFilterOptions = _leaveStatus.filterOptions;
